// C:\Users\tharu\OneDrive\Desktop\Techn Global Prod\LMS-Admin\lms-admin_dashboard\src\pages\Login\Login.jsx (Contains Sidebar)

import React, { useState } from "react";
// Import useNavigate and Link from react-router-dom
import { useNavigate, Link } from "react-router-dom";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Collapse,
  Tooltip,
  Box,
  Avatar,
  Badge,
  // --- New MUI Dialog Imports ---
  Button, // Needed for dialog actions
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  MdDashboard,
  MdPeople,
  MdSettings,
  MdChevronLeft,
  MdExpandLess,
  MdExpandMore,
  MdAssessment,
  MdCalendarToday,
  MdMonetizationOn,
  MdInfoOutline,
  MdOutlineLogout,
  MdSchool,
} from "react-icons/md";

// --- Configuration Constants ---
const fullDrawerWidth = 260;
const collapsedWidth = 72;
const TOPBAR_HEIGHT = 64;
const BRAND_ACCENT = "#10b981"; // Vibrant Emerald Green (Modern and professional)
const BRAND_DARK = "#059669";
const TEXT_COLOR = "#374151"; // Dark slate gray

const menuItems = [
  { label: "Dashboard", icon: <MdDashboard />, iconColor: "#10b981", path: "/dashboard" },
  {
    label: "Transactions",
    icon: <MdMonetizationOn />,
    iconColor: "#3b82f6",
    path: "/transactions",
  },
  {
    label: "Users",
    icon: <MdPeople />,
    iconColor: "#ef4444",
    badge: 4,
    children: [
      { label: "All Users", path: "/users" },
      { label: "User Groups", path: "/user-groups" },
      { label: "Permissions", path: "/permissions" },
    ],
  },
  {
    label: "Calendar",
    icon: <MdCalendarToday />,
    iconColor: "#f59e0b",
    path: "/calendar"
  },
  {
    label: "Course Control",
    icon: <MdSchool />,
    iconColor: "#14b8a6",
    path: "/course-control"
  },
  { label: "Reports", icon: <MdAssessment />, iconColor: "#6366f1", path: "/reports" },
  {
    label: "Support",
    icon: <MdInfoOutline />,
    iconColor: "#a855f7",
    path: "/support"
  },
  { label: "Settings", icon: <MdSettings />, iconColor: "#4b5563", path: "/settings" },
];

const Sidebar = ({ active = "Dashboard" }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState({});
  // 1. State for Dialog visibility
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
    if (!collapsed) {
      setOpenSubMenu({});
    }
  };

  const handleSubMenuToggle = (label) => {
    if (!collapsed) {
      setOpenSubMenu((prev) => ({ ...prev, [label]: !prev[label] }));
    }
  };

  // 2. Dialog open/close handlers
  const handleOpenLogoutDialog = () => {
    setOpenLogoutDialog(true);
  };

  const handleCloseLogoutDialog = () => {
    setOpenLogoutDialog(false);
  };

  // 3. Final Logout function (called only after confirmation)
  const confirmLogout = () => {
    handleCloseLogoutDialog();
    // Core Logout Logic
    localStorage.removeItem("isAuthenticated");
    navigate("/login");
  };

  // User Data for the bottom profile section
  const userName = "Jayanth";
  const userRole = "Technglobal Admin";

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: collapsed ? collapsedWidth : fullDrawerWidth,
          flexShrink: 0,
          whiteSpace: "nowrap",
          // CRITICAL FIX: Offset the drawer from the top of the viewport
          top: `${TOPBAR_HEIGHT}px`,
          height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
          "& .MuiDrawer-paper": {
            width: collapsed ? collapsedWidth : fullDrawerWidth,
            top: `${TOPBAR_HEIGHT}px`,
            height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
            backgroundColor: "#fff",
            color: TEXT_COLOR,
            borderRight: "1px solid #e5e7eb", // Soft separator
            boxShadow: "4px 0 12px rgba(0,0,0,0.05)", // Soft shadow
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)", // Smoother transition
            overflowX: "hidden",
          },
        }}
      >
        {/* --- 1. Top Logo Bar (Seamless with Topbar) --- */}
        <Box
          sx={{
            minHeight: TOPBAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: '0 16px',
            backgroundColor: '#fff', // Match main background
            color: BRAND_DARK,
            borderBottom: '1px solid #e5e7eb',
            position: 'relative',
          }}
        >
          {!collapsed ? (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.5,
                userSelect: "none",
                color: BRAND_DARK,
                display: 'flex',
                alignItems: 'center',
                fontSize: '1.1rem',
              }}
            >
              <MdDashboard size={20} style={{ marginRight: '8px', color: BRAND_ACCENT }} />
              ADMIN PANEL
            </Typography>
          ) : (
            <MdDashboard size={30} color={BRAND_ACCENT} />
          )}
        </Box>
        {/* ----------------------------------------------- */}

        {/* --- 2. Main Menu List --- */}
        <List sx={{ pt: 3, flexGrow: 1, px: 2, '&:hover': { overflowY: 'auto' } }}>
          {menuItems.map((item) => {
            const isActive = item.label === active;
            const isOpen = openSubMenu[item.label] === true;

            const renderListItemContent = (isChild = false) => (
              <ListItemButton
                onClick={item.children ? () => handleSubMenuToggle(item.label) : undefined}
                sx={{
                  justifyContent: collapsed ? "center" : "flex-start",
                  pl: isChild ? 4 : (collapsed ? 0 : 2),
                  py: 1.2,
                  borderRadius: 2.5,
                  mb: 0.5,
                  color: isActive ? BRAND_DARK : TEXT_COLOR,
                  backgroundColor: isActive ? `${item.iconColor}22` : "transparent",
                  "&:hover": {
                    backgroundColor: isActive ? `${item.iconColor}33` : '#f3f4f6',
                  },
                  transition: "background-color 0.2s ease",
                  whiteSpace: 'nowrap',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? item.iconColor : "#9ca3af",
                    minWidth: collapsed ? 0 : (isChild ? 20 : 40),
                    justifyContent: "center",
                    mr: isChild ? 1.5 : (collapsed ? 0 : 1),
                    fontSize: '1.3rem',
                  }}
                >
                  {isChild ? <span style={{ fontSize: '8px', color: '#9ca3af' }}>●</span> : (item.badge && !collapsed) ? (
                    <Badge
                      badgeContent={item.badge}
                      color="error"
                      overlap="circular"
                      sx={{ "& .MuiBadge-badge": { fontSize: "0.7rem", minWidth: 18, height: 18, fontWeight: 700 } }}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>

                {!collapsed && (
                  <>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 700 : 600,
                        fontSize: "0.9rem",
                        color: TEXT_COLOR,
                      }}
                    />
                    {item.children && (isOpen ? (
                      <MdExpandLess size={20} color={TEXT_COLOR} />
                    ) : (
                      <MdExpandMore size={20} color="#9ca3af" />
                    ))}
                  </>
                )}
              </ListItemButton>
            );

            if (item.children) {
              return (
                <React.Fragment key={item.label}>
                  {renderListItemContent()}
                  <Collapse in={isOpen && !collapsed} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ px: 1, py: 0 }}>
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.path}
                          style={{ textDecoration: "none" }}
                          tabIndex={collapsed ? -1 : 0}
                        >
                          <ListItemButton
                            sx={{
                              pl: 4,
                              py: 0.8,
                              mb: 0.3,
                              borderRadius: 2,
                              color: TEXT_COLOR,
                              "&:hover": { backgroundColor: '#eef2ff' }, // Light blue hover
                            }}
                          >
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                color: TEXT_COLOR,
                                fontSize: '0.85rem',
                                fontWeight: 500,
                              }}
                            />
                          </ListItemButton>
                        </Link>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            // Single menu items wrapper
            const menuLink = (
              <Link key={item.label} to={item.path} style={{ textDecoration: "none" }}>
                {renderListItemContent()}
              </Link>
            );

            return collapsed ? (
              <Tooltip key={item.label} title={item.label} placement="right" arrow>
                {menuLink}
              </Tooltip>
            ) : (
              menuLink
            );
          })}
        </List>

        {/* --- 3. User Profile Section (Pinned to Bottom) --- */}
        {!collapsed && (
          <Box sx={{ p: 2, borderTop: "1px solid #f3f4f6", }}>
            <ListItemButton
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: '#f9fafb',
                '&:hover': { bgcolor: '#f1f5f9' },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Avatar
                alt={userName}
                sx={{ width: 36, height: 36, bgcolor: BRAND_ACCENT }}
              >
                {userName.charAt(0)}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: TEXT_COLOR, lineHeight: 1.2 }}
                >
                  {userName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", fontSize: "0.7rem", lineHeight: 1.2 }}
                >
                  {userRole}
                </Typography>
              </Box>

              {/* FIXED: Calls the function to OPEN the dialog */}
              <IconButton
                size="small"
                sx={{ color: '#4b5563' }}
                title="Logout"
                onClick={handleOpenLogoutDialog}
              >
                <MdOutlineLogout size={20} />
              </IconButton>
            </ListItemButton>
          </Box>
        )}
      </Drawer>

      {/* --- CRITICAL: FLOATING COLLAPSE BUTTON --- */}
      <Tooltip title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"} placement="right" arrow>
        <IconButton
          onClick={toggleSidebar}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          sx={{
            position: 'fixed',
            top: `${TOPBAR_HEIGHT + 20}px`,
            left: collapsed ? `${collapsedWidth - 18}px` : `${fullDrawerWidth - 18}px`,
            zIndex: 1400,
            backgroundColor: '#fff',
            color: BRAND_ACCENT,
            boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
            width: 36,
            height: 36,
            padding: 0,
            '&:hover': {
              backgroundColor: '#fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              color: BRAND_DARK,
            },
            transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {collapsed ? <MdChevronLeft size={24} style={{ transform: 'rotate(180deg)' }} /> : <MdChevronLeft size={24} />}
        </IconButton>
      </Tooltip>
      {/* ------------------------------------------- */}

      {/* 4. LOGOUT CONFIRMATION DIALOG */}
      <Dialog
        open={openLogoutDialog}
        onClose={handleCloseLogoutDialog}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{ sx: { borderRadius: 2 } }} // Modernize the dialog box
      >
        <DialogTitle id="logout-dialog-title" sx={{ color: BRAND_DARK, fontWeight: 700 }}>
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out of the **{userName}'s** session for the Admin Panel?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseLogoutDialog}
            color="secondary"
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmLogout}
            color="error" // Use error color for the destructive action
            variant="contained"
            autoFocus
            sx={{ textTransform: 'none' }}
          >
            Log Out
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Sidebar;